import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/core/RobinPage";
import ResizableCropModal from "../components/ResizableCropModal";
import {
  useGetAllPartnerCategoriesQuery,
  useGetMyPartnerQuery,
  useUpdateMyPartnerMutation,
  useUploadFileMutation,
} from "../graphql/generated/hooks";

function SettingsPartnerInfo() {
  const { t, i18n } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const normalizeSaudiPhoneNational = (value: unknown) => {
    const digitsOnly = typeof value === "string" ? value.replace(/\D/g, "") : "";
    // With +966 prefix UI we only want the national part (9 digits), without leading 0.
    return digitsOnly.replace(/^0+/, "").slice(0, 9);
  };

  const toSaudiE164Digits = (national: unknown) => {
    const normalized = normalizeSaudiPhoneNational(national);
    return normalized ? `966${normalized}` : "";
  };

  const fromStoredSaudiPhoneToNational = (stored: unknown) => {
    const digitsOnly = typeof stored === "string" ? stored.replace(/\D/g, "") : "";
    // Accept stored formats: 05xxxxxxxx, 0xxxxxxxxx (landline), 966xxxxxxxxx, +966xxxxxxxxx
    const withoutCountry = digitsOnly.startsWith("966")
      ? digitsOnly.slice(3)
      : digitsOnly;
    return withoutCountry.replace(/^0+/, "").slice(0, 9);
  };

  const { data: partnerData, loading: loadingPartner } = useGetMyPartnerQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });
  const { data: categoriesData, loading: loadingCategories } =
    useGetAllPartnerCategoriesQuery();
  const [updatePartner, { loading: updating }] = useUpdateMyPartnerMutation();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();

  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const [uploadedImageId, setUploadedImageId] = useState<number | null>(null);

  const [coverFileList, setCoverFileList] = useState<UploadFile[]>([]);
  const [uploadedCoverImageId, setUploadedCoverImageId] = useState<number | null>(
    null
  );

  const [productFileList, setProductFileList] = useState<UploadFile[]>([]);
  const [uploadedProductImageId, setUploadedProductImageId] = useState<number | null>(
    null
  );

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState("");
  const [cropImageKind, setCropImageKind] = useState<"image" | "cover" | "product">("image");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const partnerCategoriesOptions = useMemo(
    () =>
      categoriesData?.getAllPartnerCategories?.map((partnerCategory) => ({
        label: partnerCategory.name,
        value: partnerCategory.id,
      })) || [],
    [categoriesData]
  );

  const effectivePartner = partnerData?.getPartner;

  useEffect(() => {
    if (!effectivePartner) return;

    form.setFieldsValue({
      name: effectivePartner.name,
      nameAr: effectivePartner.nameAr,
      email: effectivePartner.email,
      phone: fromStoredSaudiPhoneToNational(effectivePartner.phone),
      address: effectivePartner.address,
      partnerCategoryIds: effectivePartner.partnerCategories?.map((c) => c.id) || [],
    });

    if (effectivePartner.image) {
      setUploadedImageId(effectivePartner.image.id);
      setImageFileList([
        {
          uid: "-1",
          name: "Partner Image",
          status: "done",
          url: effectivePartner.image.url,
        },
      ]);
    } else {
      setUploadedImageId(null);
      setImageFileList([]);
    }

    if (effectivePartner.coverImage) {
      setUploadedCoverImageId(effectivePartner.coverImage.id);
      setCoverFileList([
        {
          uid: "-2",
          name: "Partner Cover Image",
          status: "done",
          url: effectivePartner.coverImage.url,
        },
      ]);
    } else {
      setUploadedCoverImageId(null);
      setCoverFileList([]);
    }

    if (effectivePartner.productImage) {
      setUploadedProductImageId(effectivePartner.productImage.id);
      setProductFileList([
        {
          uid: "-3",
          name: "Partner Product Image",
          status: "done",
          url: effectivePartner.productImage.url,
        },
      ]);
    } else {
      setUploadedProductImageId(null);
      setProductFileList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePartner]);

  const convertFileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const handleFileSelect = (kind: "image" | "cover" | "product") => (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      setCropImageUrl(imageUrl);
      setCropImageKind(kind);
      setPendingFile(file);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    return false; // Prevent default upload
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      const base64String = await convertFileToBase64(croppedBlob);
      const fileName = pendingFile?.name || `cropped-${Date.now()}.jpg`;

      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: fileName,
          folder:
            cropImageKind === "cover"
              ? "partners/covers"
              : cropImageKind === "product"
                ? "partners/products"
                : "partners",
        },
      });

      if (!result.data?.uploadFile) {
        throw new Error("Upload failed: No data returned");
      }

      const uploaded = result.data.uploadFile;

      if (cropImageKind === "cover") {
        setUploadedCoverImageId(uploaded.id);
        setCoverFileList([
          {
            uid: `-cover-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      } else if (cropImageKind === "product") {
        setUploadedProductImageId(uploaded.id);
        setProductFileList([
          {
            uid: `-product-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      } else {
        setUploadedImageId(uploaded.id);
        setImageFileList([
          {
            uid: `-image-${uploaded.id}`,
            name: fileName,
            status: "done",
            url: uploaded.url,
          },
        ]);
      }

      setCropModalOpen(false);
      setPendingFile(null);
      setCropImageUrl("");
      messageApi.success(t("message.success.data_updated"));
    } catch (error) {
      messageApi.error(t("message.error.data_update_failed"));
      setCropModalOpen(false);
      setPendingFile(null);
      setCropImageUrl("");
    }
  };

  const handleEditImage = async (kind: "image" | "cover" | "product") => {
    let imageUrl = "";
    if (kind === "image" && imageFileList[0]?.url) {
      imageUrl = imageFileList[0].url;
    } else if (kind === "cover" && coverFileList[0]?.url) {
      imageUrl = coverFileList[0].url;
    } else if (kind === "product" && productFileList[0]?.url) {
      imageUrl = productFileList[0].url;
    }

    if (!imageUrl) return;

    setCropImageKind(kind);

    // For remote URLs: fetch and convert to data URL so cropping works (no tainted canvas).
    // If fetch fails (e.g. no CORS), we still open with URL so user can view; Done will show a warning.
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      try {
        const res = await fetch(imageUrl, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        setCropImageUrl(dataUrl);
        setCropModalOpen(true);
      } catch {
        // CORS or network error: open with URL so image at least displays; Done will show warning
        setCropImageUrl(imageUrl);
        setCropModalOpen(true);
      }
    } else {
      setCropImageUrl(imageUrl);
      setCropModalOpen(true);
    }
  };

  const onFinish = async (values: any) => {
    const phoneE164 = toSaudiE164Digits(values.phone);
    await updatePartner({
      variables: {
        name: values.name,
        nameAr: values.nameAr,
        email: values.email,
        phone: phoneE164 || null,
        address: values.address,
        partnerCategoryIds: values.partnerCategoryIds,
        imageId: uploadedImageId,
        coverImageId: uploadedCoverImageId,
        productImageId: uploadedProductImageId,
      },
      refetchQueries: ["getMyPartner"],
      awaitRefetchQueries: true,
    })
      .then(() => {
        messageApi.success(t("message.success.data_updated"));
        navigate(-1);
      })
      .catch(() => {
        messageApi.error(t("message.error.data_update_failed"));
      });
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("button.upload")}</div>
    </button>
  );

  if (loadingPartner && !effectivePartner) {
    return (
      <RobinPage title={t("title.partner_info")}>
        <Spin />
      </RobinPage>
    );
  }

  return (
    <RobinPage title={t("title.partner_info")}>
      {contextHolder}
      <Row gutter={[16, 16]} dir={i18n.dir()}>
        <Col span={24}>
          <Form form={form} layout="vertical" onFinish={onFinish} name="partnerInfo">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label={t("label.name")}
                  rules={[{ required: true, message: t("message.error.name_required") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nameAr"
                  label={t("label.name_ar")}
                  rules={[{ required: true, message: t("message.error.name_required") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label={t("label.email")}
                  rules={[{ type: "email", message: t("message.error.email_required") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="phone"
                  label={t("label.phone")}
                  normalize={normalizeSaudiPhoneNational}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("validation.phone.required") || "Phone is required",
                    },
                    {
                      validator: async (_rule, value) => {
                        const raw = typeof value === "string" ? value.trim() : "";
                        if (!raw) return Promise.resolve(); // handled by `required`

                        const saudiPhoneDigitsOnly = /^(5\d{8}|[1-4,6-7]\d{8})$/;
                        if (!/^\d+$/.test(raw) || !saudiPhoneDigitsOnly.test(raw)) {
                          return Promise.reject(
                            new Error(
                              t("validation.phone.invalid") ||
                                "Phone must be a valid Saudi mobile or landline number"
                            )
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    addonBefore="+966"
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="5XXXXXXXX or 1XXXXXXXX"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="address" label={t("label.address")}>
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label={t("label.partner.image")}>
                  <Upload
                    listType="picture-card"
                    fileList={imageFileList}
                    beforeUpload={(file) => handleFileSelect("image")(file)}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList[0]?.status === "done" || newFileList.length === 0) {
                        setImageFileList(newFileList);
                      }
                    }}
                    onRemove={() => {
                      setUploadedImageId(null);
                      setImageFileList([]);
                    }}
                    onPreview={() => handleEditImage("image")}
                    maxCount={1}
                    accept="image/*"
                  >
                    {imageFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={t("label.partner.cover_image")}>
                  <Upload
                    listType="picture-card"
                    fileList={coverFileList}
                    beforeUpload={(file) => handleFileSelect("cover")(file)}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList[0]?.status === "done" || newFileList.length === 0) {
                        setCoverFileList(newFileList);
                      }
                    }}
                    onRemove={() => {
                      setUploadedCoverImageId(null);
                      setCoverFileList([]);
                    }}
                    onPreview={() => handleEditImage("cover")}
                    maxCount={1}
                    accept="image/*"
                  >
                    {coverFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={t("label.partner.product_image")}>
                  <Upload
                    listType="picture-card"
                    fileList={productFileList}
                    beforeUpload={(file) => handleFileSelect("product")(file)}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList[0]?.status === "done" || newFileList.length === 0) {
                        setProductFileList(newFileList);
                      }
                    }}
                    onRemove={() => {
                      setUploadedProductImageId(null);
                      setProductFileList([]);
                    }}
                    onPreview={() => handleEditImage("product")}
                    maxCount={1}
                    accept="image/*"
                  >
                    {productFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="partnerCategoryIds"
              label={t("label.partner.category")}
              rules={[
                { required: true, message: t("message.error.field_required") },
              ]}
            >
              <Select mode="multiple" loading={loadingCategories} options={partnerCategoriesOptions} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={() => navigate(-1)}>{t("button.cancel")}</Button>
                <Button
                  loading={updating || uploading}
                  type="primary"
                  htmlType="submit"
                >
                  {t("button.save")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <ResizableCropModal
        open={cropModalOpen}
        imageUrl={cropImageUrl}
        shape={
          cropImageKind === "cover"
            ? "rectangle"
            : cropImageKind === "product"
              ? "square"
              : null
        }
        onCancel={() => {
          setCropModalOpen(false);
          setPendingFile(null);
          setCropImageUrl("");
        }}
        onOk={handleCropComplete}
      />
    </RobinPage>
  );
}

export default SettingsPartnerInfo;

